import {
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  Injector,
  input,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core'
import {faCheck} from '@fortawesome/pro-solid-svg-icons/faCheck'
import {faExclamation} from '@fortawesome/pro-solid-svg-icons/faExclamation'
import {faFilePdf} from '@fortawesome/pro-solid-svg-icons/faFilePdf'
import {faFileArrowDown} from '@fortawesome/pro-solid-svg-icons/faFileArrowDown'
import {NG_VALUE_ACCESSOR} from '@angular/forms'
import {ControlValueAccessorConnectorDirective} from '../forms/control-value-accessor-connector.directive'
import {FileModel} from './file.model'
import {Observable} from 'rxjs'
import {faPlus} from '@fortawesome/pro-regular-svg-icons/faPlus'
import {faTrashAlt} from '@fortawesome/pro-regular-svg-icons/faTrashAlt'
import {Logger} from '../../core/logging/logger.types'
import {GenerateFileUploadLinkFunction} from './file.service.types'
import {FileService} from './file.service'
import {Gallery} from 'ng-gallery'
import {transformBuildigoImageUrl} from '../../core/picture/loaders/buildigo-image-api'
import {Lightbox} from 'ng-gallery/lightbox'

@Component({
  selector: 'cft-files',
  templateUrl: './files.component.html',
  styleUrls: ['./files.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: FilesComponent,
      multi: true,
    },
  ],
})
export class FilesComponent extends ControlValueAccessorConnectorDirective implements OnDestroy {
  /**
   * See FileInputComponent.accept for more information.
   */
  accept = input<string[]>(['image/png', 'image/jpeg', 'application/pdf'])

  @Input() canOpenFile = false // always true for files without preview (such as pdf)
  @Input() canSelectMultiple = true // n/a if canUpload is false
  @Input() generateUploadLink?: GenerateFileUploadLinkFunction
  @Input() deleteFile?: (file: FileModel) => Observable<FileDeleteResult>
  @Input() uploadLabel?: string
  @Input() id?: string

  @Output() fileUploaded = new EventEmitter<FileModel>()

  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>

  protected supportedExtensions = computed(() => FileService.extensions(this.accept()).join(', '))

  hasRemovalErrors = false
  readonly uploadedIcon = faCheck
  readonly errorIcon = faExclamation
  readonly pdfIcon = faFilePdf
  readonly otherFileIcon = faFileArrowDown
  readonly uploadIcon = faPlus
  readonly removeIcon = faTrashAlt
  private readonly galleryId = 'file-uploads-gallery'

  // control value
  get selectedFiles(): FileModel[] {
    return this.control?.value || []
  }

  get canUpload(): boolean {
    return (
      !!this.generateUploadLink &&
      ((!this.canSelectMultiple && this.selectedFiles.length === 0) || this.canSelectMultiple)
    )
  }

  get canDelete(): boolean {
    return !!this.deleteFile
  }

  constructor(
    injector: Injector,
    private readonly gallery: Gallery,
    private readonly lightbox: Lightbox,
    private readonly cdr: ChangeDetectorRef,
    private readonly logger: Logger,
  ) {
    super(injector)
  }

  ngOnDestroy() {
    this.gallery.ref(this.galleryId).destroy()
  }

  get hasUploadErrors(): boolean {
    return this.selectedFiles?.some(f => f.error)
  }

  removeFile(file: FileModel): void {
    if (this.deleteFile && file.uploaded && file.fileKey) {
      this.deleteFile(file).subscribe(({success, err}) => {
        if (!success) {
          this.logger.error('Error removing file.', {err})
          this.hasRemovalErrors = true
          this.control?.setValue(this.control?.value) // trigger valueChanges event
          return
        }

        const selectedFiles = this.control?.value
        const index = selectedFiles.indexOf(file)
        selectedFiles.splice(index, 1)
        this.control?.setValue(selectedFiles)

        if (this.fileInput?.nativeElement?.value) {
          this.fileInput.nativeElement.value = ''
        }
        this.cdr.markForCheck()
      })
    }
  }

  setFileWasUploaded(e: FileModel) {
    this.control?.setValue(this.selectedFiles)
    this.fileUploaded.emit(e)
  }

  imageLoadedOnPage(file: FileModel) {
    if (file.revokeUrlOnLoad) {
      URL.revokeObjectURL(file.url)
    }
  }

  openGallery(selectedImage: FileModel) {
    const files = this.selectedFiles.filter(f => f.contentType.includes('image/'))
    const index = files.findIndex(f => f.fileKey === selectedImage.fileKey)
    const gallery = this.gallery.ref(this.galleryId)
    gallery.setConfig({
      imageSize: 'contain',
    })
    gallery.load(
      files.map(i => ({
        type: 'image',
        data: {
          src: i.url,
          thumb: i.url.startsWith('blob') ? i.url : transformBuildigoImageUrl(i.url, {width: 200}),
        },
      })),
    )

    this.lightbox.open(index, this.galleryId)
  }
}

export interface FileDeleteResult {
  success: boolean
  err: string
}
